import React from 'react'
import type { PushAudience } from '@/shared/api'
import { Input } from '@/shared/ui'

// Ограничение длины текста пуша в символах (эмодзи считается одним) — как в бэкенде
export const MAX_MESSAGE_LENGTH = 150

export const AUDIENCE_OPTIONS: Array<{ value: PushAudience; label: string }> = [
  { value: 'all', label: 'Все курьеры' },
  { value: 'staff', label: 'Штатные курьеры' },
  { value: 'external', label: 'Сторонние курьеры' },
]

export interface PushCampaignValues {
  title: string
  message: string
  audience: PushAudience
}

export const emptyPushCampaign = (): PushCampaignValues => ({
  title: '',
  message: '',
  audience: 'all',
})

/** Длина в символах, а не в единицах UTF-16: эмодзи не должен «съедать» два */
export const pushMessageLength = (message: string): number => [...message].length

export const isPushCampaignValid = (values: PushCampaignValues): boolean => {
  const length = pushMessageLength(values.message)
  return (
    values.title.trim().length > 0 &&
    values.message.trim().length > 0 &&
    length <= MAX_MESSAGE_LENGTH
  )
}

interface PushCampaignFormProps {
  values: PushCampaignValues
  onChange: (patch: Partial<PushCampaignValues>) => void
  /** id формы: внешняя кнопка «Отправить» в подвале модалки отправляет её */
  formId: string
  disabled?: boolean
  /** Ошибка запроса числа получателей (например, «Нет курьеров…») */
  errorMessage?: string | null
  onSubmit: () => void
}

/** Поля новой рассылки без собственной кнопки отправки — используется в модалке */
export const PushCampaignForm: React.FC<PushCampaignFormProps> = ({
  values,
  onChange,
  formId,
  disabled = false,
  errorMessage = null,
  onSubmit,
}) => {
  const messageLength = pushMessageLength(values.message)
  const isMessageTooLong = messageLength > MAX_MESSAGE_LENGTH

  return (
    <form
      id={formId}
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <Input
        label="Заголовок"
        placeholder="Например, «Важно: обновите приложение»"
        value={values.title}
        maxLength={255}
        disabled={disabled}
        onChange={(event) => onChange({ title: event.target.value })}
      />

      <div className="space-y-1">
        <label
          className="block text-sm font-medium text-gray-700"
          htmlFor="push-message"
        >
          Текст уведомления
        </label>
        <textarea
          id="push-message"
          className="admin-input"
          rows={4}
          placeholder="Короткое сообщение для курьеров"
          value={values.message}
          disabled={disabled}
          onChange={(event) => onChange({ message: event.target.value })}
        />
        <p
          className={
            isMessageTooLong
              ? 'text-sm text-error-600'
              : 'text-xs text-gray-500'
          }
        >
          {messageLength} / {MAX_MESSAGE_LENGTH}
          {isMessageTooLong && ' — текст слишком длинный'}
        </p>
      </div>

      <fieldset className="space-y-2" disabled={disabled}>
        <legend className="block text-sm font-medium text-gray-700">
          Аудитория
        </legend>
        {AUDIENCE_OPTIONS.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-2 text-sm text-gray-700"
          >
            <input
              type="radio"
              name="push-audience"
              className="h-4 w-4 border-gray-300 text-gray-700 focus:ring-gray-500"
              value={option.value}
              checked={values.audience === option.value}
              onChange={() => onChange({ audience: option.value })}
            />
            {option.label}
          </label>
        ))}
      </fieldset>

      {errorMessage && <p className="text-sm text-error-600">{errorMessage}</p>}
    </form>
  )
}
